import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { buildSessionHandoffUrl, getIDPProviders, getSessionStatus, shouldUseSessionHandoff } from '../api/auth'
import { LoginAnimations } from '../components/LoginAnimations/LoginAnimations'
import { FeatureBubbles } from '../components/FeatureShowcase/FeatureBubbles'
import { LANDING_FEATURES } from '../components/FeatureShowcase/features'
import { LoginActions } from '../components/LoginActions/LoginActions'
import { BrandingLogo } from '../components/BrandingLogo'
import { getAppName, getAppSubName } from '../config/appName'
import styles from './LoginPage.module.css'

export default function LoginPage() {
  const [searchParams] = useSearchParams()
  const info = searchParams.get('info')
  const redirectParam = searchParams.get('redirect')
  const [homeAppUrl, setHomeAppUrl] = useState<string | null>(null)
  const [appName, setAppName] = useState(getAppName)
  const [appSubName, setAppSubName] = useState(getAppSubName)
  const [configError, setConfigError] = useState<string | null>(null)
  const [returning, setReturning] = useState(false)

  useEffect(() => {
    getIDPProviders()
      .then((data) => {
        setAppName(getAppName(data.app_name))
        setAppSubName(getAppSubName(data.app_sub_name))
        if (data.home_app_url) {
          setHomeAppUrl(data.home_app_url)
        } else {
          setConfigError('Home app URL is not configured on the auth service.')
        }
      })
      .catch(() => {
        setConfigError('Unable to load login configuration. Please try again.')
      })
  }, [])

  useEffect(() => {
    document.title = appName
  }, [appName])

  useEffect(() => {
    if (!homeAppUrl) return
    let cancelled = false

    const maybeReturn = async () => {
      try {
        const status = await getSessionStatus()
        if (cancelled || !status.valid) {
          return
        }
        setReturning(true)
        if (redirectParam && shouldUseSessionHandoff(redirectParam, homeAppUrl)) {
          window.location.replace(buildSessionHandoffUrl(redirectParam))
          return
        }
        if (redirectParam) {
          window.location.replace(redirectParam)
          return
        }
        window.location.replace(homeAppUrl)
      } catch {
        // Invalid or unreachable session — stay on the login page.
      }
    }

    void maybeReturn()
    return () => {
      cancelled = true
    }
  }, [homeAppUrl, redirectParam])

  if (configError) {
    return (
      <div className={styles.loginPage}>
        <p className={styles.subtitle}>{configError}</p>
        <button type="button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    )
  }

  if (!homeAppUrl || returning) return null

  return (
    <div className={styles.loginPage}>
      <LoginAnimations isVisible={true} />

      {info === 'mail_connect' && (
        <div className={styles.infoBanner} role="status">
          Mail needs a Microsoft sign-in. Use Login with your Microsoft account (not email/password).
        </div>
      )}

      {info === 'session_expired' && (
        <div className={styles.infoBanner} role="alert">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          Your session expired or you were signed out in another tab.
        </div>
      )}

      <div className={styles.logoContainer}>
        <BrandingLogo alt={appName} className={styles.logo} />
      </div>

      <LoginActions />

      <div className={styles.header}>
        <h2 className={styles.title}>{appName}</h2>
        <p className={styles.subtitle}>
          {appSubName}
        </p>
      </div>

      <FeatureBubbles
        features={LANDING_FEATURES.map((feature) =>
          feature.id === 'deepSynthesis'
            ? { ...feature, title: `${appName} Engine` }
            : feature,
        )}
        isActive={true}
      />
    </div>
  )
}
